import React from "react";

export const EXPERT_VIDEOS: TutorialVideo[] = [
    {
        id: 1,
        title: "About The Desire Company's Expert Community",
        desc: "Get to know The Desire Company's expert community and how it works",
        duration: "",
        src: "https://desirelist-site-videos.s3.us-east-1.amazonaws.com/FAQ-5-About-Expert-Community_v6_2-lowres.mp4",
    },
    {
        id: 2,
        title: "Filming with TDC",
        desc: "Walk through what to expect when filming with TDC",
        duration: "",
        src: "https://desirelist-site-videos.s3.us-east-1.amazonaws.com/Filming-with-TDC-2025_16x9-lowres.mp4",
    },
];

export interface FaqEntry {
    id: number;
    q: string;
    a: string;
}

export const FAQS: FaqEntry[] = [
    {
        id: 1,
        q: "What can I expect during my day on set?",
        a: "Upon arrival, your producer will greet you and walk through the day's schedule and the shoot's creative. After selecting wardrobe, you will usually start with your interview, or the speaking portion of the shoot. From there, you will film b-roll, or supplemental footage, that brings to life what was said during the interview."
    },
    {
        id: 2,
        q: "How long is the shoot day?",
        a: "Your producer will confirm your production details, but expect to be on set for 4-6 hours. Lunch will be provided."
    },
    {
        id: 3,
        q: "What do I need to bring?",
        a: "Your producer will provide you a final checklist, but usually, experts are asked to bring 2-3 wardrobe options, any specific personal props needed for b-roll, and the product you've been testing."
    },
    {
        id: 4,
        q: "How do I prepare for the shoot?",
        a: "Once selected for a project, you will have a kickoff call with your producer. They will give you an overview of our production process & what to expect. Generally, to prepare for your shoot, you will need to thoroughly test the product you are set to review, complete our product feedback form, and read your final \"reminders\" email before arriving to set. Please also be sure to complete your SOW in Docusign. During this pre-production process, it is helpful if your communication response time is within 48 hours."
    },
    {
        id: 5,
        q: "How do I book travel?",
        a: "Your associate producer will always provide travel instructions, but usually, you will book flights and hotel stays through Navan. Any specific guidelines or budget constraints will be set there, and our company credit card will pay for these purchases. Once on the ground, transportation to and from the airport and set is via Uber or your preferred ride share app, and these costs will be reimbursed."
    },
    {
        id: 6,
        q: "What costs does The Desire Company cover while I travel?",
        a: "After booking your flight & hotel on Navan, please keep your personal receipts for parking, ubering to/from set, and meals. We will reimburse those after the trip concludes. You are permitted to spend up to $75 a day on food and beverage."
    },
    {
        id: 7,
        q: "Do you have hair stylists and makeup artists on set?",
        a: "We do not provide hair and makeup services. We usually ask that experts arrive \"camera-ready\" and bring any necessary supplies for hair and makeup touchups."
    },
    {
        id: 8,
        q: "How do I get paid?",
        a: "After the shoot, you will need to sign a W-9 and submit any travel receipts for reimbursement in order for your payment to be processed. Once we have these items, you can expect a direct deposit or check within 7-10 business days."
    }
];

export const PRODUCTION_FAQ_DOC = {
    name: "TDC Production FAQ's",
    meta: "PDF",
    url: "/TDC-Production-FAQ.pdf",
    filename: "TDC-Production-FAQ.pdf"
};

const FAQ_LINKS: Record<string, string> = {
    Navan: "https://navan.com/"
};

export function renderFaqAnswer(text: string): React.ReactNode {
    const keys = Object.keys(FAQ_LINKS);
    if (keys.length === 0) return text;
    const pattern = new RegExp(`(${keys.join("|")})`, "g");
    const parts = text.split(pattern);
    return parts.map((part, i) => {
        const href = FAQ_LINKS[part];
        if (href) {
            return (
                <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text underline"
                >
                    {part}
                </a>
            );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

export interface TutorialVideo {
    id: number;
    title: string;
    desc: string;
    duration: string;
    src: string;
}

export const TUTORIAL_VIDEOS: TutorialVideo[] = [
    {
        id: 1,
        title: "Video Review & Feedback",
        desc: "Learn how to review videos and provide feedback through the portal",
        duration: "4:32",
        src: "https://desirelist-site-videos.s3.us-east-1.amazonaws.com/reviewing.mp4"
    },
    {
        id: 2,
        title: "Accessing & Downloading Final Content",
        desc: "How to access and download your final approved content",
        duration: "3:18",
        src: "https://desirelist-site-videos.s3.us-east-1.amazonaws.com/downloading.mp4"
    },
    {
        id: 3,
        title: "Agentic Performance Video",
        desc: "Setting up and managing your Shopify agentic shelf",
        duration: "5:47",
        src: "https://desirelist-site-videos.s3.us-east-1.amazonaws.com/agentic-json-ld.mp4"
    }
];

export interface GettingStartedFaq {
    id: number;
    q: string;
    a: string;
}

export const GETTING_STARTED_FAQS: GettingStartedFaq[] = [
    {
        id: 1,
        q: "How do I navigate the portal?",
        a: "After logging in, you'll see your main dashboard with Projects, Videos, Brands, and Profile tabs. Use the top navigation to switch between sections. Each project contains all relevant videos, experts, and settings."
    },
    {
        id: 2,
        q: "How do I review a video?",
        a: "Navigate to your project, find the video you'd like to review, and click on it. You can leave timestamped comments, approve, or request revisions directly from the video player."
    },
    {
        id: 3,
        q: "How do I download final content?",
        a: "Go to the Videos section, find your approved video, and click the download icon. You can choose between different formats and resolutions depending on your needs."
    },
    {
        id: 4,
        q: "How do I manage my brand settings?",
        a: "Click on Brands in the top navigation, select your brand, and you'll find all settings including team members, brand guidelines, and product catalog."
    }
];

export const SUPPORT_EMAIL = "help@thedesirecompany.com";
